import { AppState } from '@store/state';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Component, OnInit, Inject, OnDestroy, ViewChild, TemplateRef, AfterViewInit } from '@angular/core';
import { ErrorContent } from '@shared/services/content/_interfaces_/content.interface';
import { ErrorModalModel, ErrorModalContent } from '@store/error-modal/error-modal.model';
import { selectErrorMessagesList, selectErrorModal } from '@store/error-modal/error-modal.selectors';
import { Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import * as errorModalActions from '@store/error-modal/error-modal.actions';
import * as fullscreenLoaderActions from '@store/fullscreen-loader/fullscreen-loader.actions';
import { ContentFieldsEnum } from '@enums/content-fields.enum';
import { ErrorModalService } from './error-modal.service';

@Component({
  selector: 'app-error-modal',
  templateUrl: './error-modal.component.html',
  styleUrls: ['./error-modal.component.scss'],
})
export class ErrorModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('errorModalTemplate') errorModalTemplate: TemplateRef<any>;
  private unsubscribeAll$: Subject<void> = new Subject();
  public error: ErrorModalModel;
  public errorContent: ErrorModalContent;
  public errorModalShown: boolean = false;
  public modalRef: BsModalRef;
  private terminateSubscription = new Subject();

  constructor(
    @Inject(BsModalService) private _modalService: BsModalService,
    @Inject(Store) private _store: Store<AppState>,
    @Inject(ErrorModalService) private errorModalService: ErrorModalService
  ) {}

  ngOnInit(): void {
    this.subscribeToModalCloseByBackdropClick();
  }

  ngAfterViewInit(): void {
    this.subscribeToErrorModalState().subscribe((data) => {
      if (data.isVisible) {
        this.getModalContent(data)
          .pipe(takeUntil(this.terminateSubscription))
          .subscribe(() => {
            this.showModal();
          });
      }
    });
  }

  private emitTerminateSubscription() {
    this.terminateSubscription.next();
  }

  private subscribeToModalCloseByBackdropClick(): void {
    this._modalService.onHidden.pipe(takeUntil(this.unsubscribeAll$)).subscribe(() => {
      if (this.errorModalShown) {
        this._store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible: false }));
        this.errorModalShown = false;
      }
    });
  }

  private getModalContent(error: ErrorModalModel): Observable<any> {
    return this._store.select(selectErrorMessagesList).pipe(
      map((errors: ErrorModalContent[]) => {
        if (errors && error.errorCode) {
          const errorByCode = errors.find((e: ErrorModalContent) => e.code === error.errorCode);
          this.setModalContent(
            errorByCode ? errorByCode : errors?.find((e: ErrorModalContent) => e.code === ContentFieldsEnum.Generic),
            error
          );
        } else {
          this.setModalContent(
            errors?.find((e: ErrorModalContent) => e.code === ContentFieldsEnum.Generic),
            error
          );
        }
      })
    );
  }

  private setModalContent(resp: ErrorContent, err: ErrorModalModel) {
    this.error = err;
    this.errorContent = resp ? resp : err.errorToShow;
  }

  public subscribeToErrorModalState(): Observable<ErrorModalModel> {
    return this._store.select(selectErrorModal).pipe(takeUntil(this.unsubscribeAll$));
  }

  public showModal(): void {
    this.errorModalShown = true;
    this.modalRef = this._modalService.show(this.errorModalTemplate, { class: 'modal-dialog-centered' });
    this.emitTerminateSubscription();
  }

  public handleClose(): void {
    this.modalRef.hide();
    this._store.dispatch(errorModalActions.setVisiblity({ isVisible: false }));
    this._store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible: false }));
    this.errorModalService.handleClose();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }
}
